// user.service.js
import config from 'app/config';

// Define constants for better readability and easier updates
const HS_CONTACT_API_BASE = config.api + 'hs/contact/';
const HS_FORM_API_BASE = config.api + 'hs/form/';
const API_V3_BASE = config.apiv3;
const HUBSPOT_UTK_COOKIE_NAME = 'hubspotutk';

class userService { // Class name kept as 'userService'

  // Ensure all dependencies are correctly injected for minification
  static $inject = ['$http', '$location', '$state', '$q']; // Injected $q for promises

  /** @ngInject */
  constructor($http, $location, $state, $q) {

    Object.assign(this, {
      $http, $location, $state, $q
    });

    this.user = {};
    this.qParams = this.getQueryParamObject();
    this.email = this.hasProperty(this.qParams, 'email') ? this.qParams.email : null; // Already decoded by getQueryParamObject
  }

  /**
   * Reads all query parameters from the URL and returns as an object.
   * @returns {Object} An object containing key-value pairs of query parameters.
   */
  getQueryParamObject() {
    return Object.fromEntries(new URLSearchParams(window.location.search).entries());
  }

  /**
   * Retrieves a specific query parameter by key.
   * @param {string} key The key of the query parameter to retrieve.
   * @param {any} [dvalue=''] The default value to return if the key is not found.
   * @returns {string|any} The value of the query parameter or the default value.
   */
  getQueryParam(key, dvalue = '') {
    return this.qParams[key] ?? dvalue; // '??' is ES2020, use '||' if transpilation is basic
  }

  /**
   * Checks if an object has a specific own property.
   * @param {Object} object The object to check.
   * @param {string} key The key to check for.
   * @returns {boolean} True if the object has the property, false otherwise.
   */
  hasProperty(object, key) {
    return Object.hasOwn ? Object.hasOwn(object, key) : Object.prototype.hasOwnProperty.call(object, key);
  }

  /**
   * Returns a property value from an object, with a safety check.
   * @param {Object} object The object to retrieve the property from.
   * @param {string} key The key of the property to retrieve.
   * @returns {any} The value of the property, or an empty string if not found.
   */
  getProperty(object, key) {
    return this.hasProperty(object, key) ? object[key] : "";
  }

  /**
   * Retrieves the 'value' property from a nested object property.
   * Assumes the structure { propertyName: { value: 'someValue' } }.
   * @param {Object} obj The parent object.
   * @param {string} property The name of the property to check.
   * @returns {any} The 'value' of the property, or an empty string if not found.
   */
  checkProperty(obj, property) {
    return obj?.[property]?.value || "";
  }

  /**
   * Fetches user data from HubSpot by email.
   * @param {string} email The email of the user.
   * @returns {Promise} An AngularJS $http promise.
   */
  getUser(email) {
    if (!email) {
      console.error("UserService.getUser: Email is required.");
      return this.$q.reject(new Error("Email parameter is missing for getUser."));
    }
    // Encode email here as it's a URL path segment and might contain special characters
    return this.$http.get(HS_CONTACT_API_BASE + encodeURIComponent(email));
  }

  /**
   * Fetches a generic row/data from the APIv3 endpoint.
   * @param {string} endpoint The specific endpoint path (e.g., 'products/123').
   * @returns {Promise} An AngularJS $http promise.
   */
  getRow(endpoint) {
    if (!endpoint) {
      console.error("UserService.getRow: Endpoint is required.");
      return this.$q.reject(new Error("Endpoint parameter is missing for getRow."));
    }
    return this.$http.get(API_V3_BASE + endpoint);
  }

  /**
   * Fetches user data from HubSpot either by email (from query params) or HubSpot cookie.
   * @returns {Promise} An AngularJS $http promise.
   */
  getUserByEmailOrToken() {
    // this.email is already decoded from qParams
    const contactIdentifier = this.email || this.getCookie(HUBSPOT_UTK_COOKIE_NAME);

    if (!contactIdentifier) {
      console.warn("UserService.getUserByEmailOrToken: No email or HubSpot cookie found to fetch user data.");
      return this.$q.reject(new Error("No valid contact identifier found."));
    }
    return this.$http.get(HS_CONTACT_API_BASE + encodeURIComponent(contactIdentifier));
  }

  /**
   * Merges HubSpot user properties into a given form object.
   * @param {Object} form The form object to merge properties into.
   * @returns {Promise<Object>} A promise that resolves with the modified form object.
   */
  async mergeHSUser(form) {
    if (!this.email) {
      console.log("UserService.mergeHSUser: No email found in query parameters. Skipping HubSpot user merge.");
      return form; // Return form as is if no email to fetch user data
    }

    form.email = this.email; // Ensure form has the email

    try {
      const result = await this.getUser(this.email); // Await the promise from getUser

      if (result.data && result.data.status === 'ok' && result.data.data) {
        this.user = result.data.data;
        const properties = this.user.properties;

        // Use a simple object for the mapping, more efficient than creating a Map for this size
        const mrgprty = {
          'billToFirstName': 'firstname',
          'billToLastName': 'lastname',
          'billToAddress1': 'address',
          'billToCity': 'city',
          'billToState': 'state',
          'billToPostalCode': 'zip',
          'billToCountry': 'country',
          'billToDayPhone': 'home_phone'
        };

        // Use Object.entries for iterating over properties safely and cleanly
        for (const [formKey, hsPropertyKey] of Object.entries(mrgprty)) {
          // Use checkProperty to get the value safely
          form[formKey] = this.checkProperty(properties, hsPropertyKey);
        }
      } else {
        console.warn('UserService.mergeHSUser: Failed to fetch user data or status not "ok". Message:', result.data?.message || 'Unknown error');
      }
    } catch (error) {
      console.error('UserService.mergeHSUser: Error fetching or merging HubSpot user:', error);
      // Decide whether to re-throw or just log. For services, logging might be enough.
    }
    return form;
  }

  /**
   * Prefills HTML form elements based on HubSpot properties.
   * This method performs direct DOM manipulation.
   * @param {string} target The ID of the HTML element containing the form.
   * @param {Array<string>} preprop An array of strings representing form fields to prefill.
   * Each string can be 'type:propertyName' or 'propertyName'.
   * @param {Object} properties The HubSpot properties object (e.g., this.user.properties).
   */
  prefillHSForm(target, preprop, properties) {
    const prefillelm = document.getElementById(target);
    if (!prefillelm) {
      console.error(`UserService.prefillHSForm: Target element with ID '${target}' not found.`);
      return;
    }
    if (!Array.isArray(preprop) || !properties) {
      console.warn("UserService.prefillHSForm: Invalid 'preprop' array or 'properties' object provided.");
      return;
    }

    preprop.forEach(propString => {
      let type = null;
      let propertyName = propString;

      // Parse 'type:propertyName' format
      if (propString.includes(':')) {
        [type, propertyName] = propString.split(':');
      }

      // Safely get the value using checkProperty
      const valueToFill = this.checkProperty(properties, propertyName);

      // Only attempt to fill if a valid value is found
      if (valueToFill === "") { // checkProperty returns "" if not found
        return;
      }

      let element;

      switch (type) {
        case 'textarea': {
          const element = prefillelm.querySelector(`textarea[name="${propertyName}"]`); // Declare element within block
          if (element) {
            element.value = valueToFill;
            element.dispatchEvent(new Event('input', {
              bubbles: true
            })); // Simulate user input
          }
          break;
        }
        case 'radio': {
          // Select the specific radio button by its value
          const element = prefillelm.querySelector(`input[type="radio"][name="${propertyName}"][value="${valueToFill}"]`); // Declare element within block
          if (element) {
            element.checked = true; // Directly set checked property
            element.dispatchEvent(new Event('change', {
              bubbles: true
            })); // Dispatch change event
          }
          break;
        }
        case 'checkbox': { // <-- ADDED CULRY BRACES HERE
          // Split values by semicolon and filter out empty strings
          const checkvalues = String(valueToFill).split(';').filter(e => e.trim().length > 0);
          checkvalues.forEach(cvalue => {
            const checkboxElm = prefillelm.querySelector(`input[type="checkbox"][name="${propertyName}"][value="${cvalue}"]`);
            if (checkboxElm) {
              checkboxElm.checked = true;
              checkboxElm.dispatchEvent(new Event('change', {
                bubbles: true
              })); // Dispatch change event
            }
          });
          break;
        } // <-- AND HERE
        case 'select': { // <-- ADDED CULRY BRACES HERE for consistency
          const element = prefillelm.querySelector(`select[name="${propertyName}"]`); // Declare element within block
          if (element) {
            element.value = valueToFill;
            element.dispatchEvent(new Event('change', {
              bubbles: true
            })); // Dispatch change event
          }
          break;
        } // <-- AND HERE
        default: { // Default case for text, number, email inputs - ADDED CULRY BRACES for consistency
          const element = prefillelm.querySelector(`input[name="${propertyName}"]`); // Declare element within block
          if (element) {
            element.value = valueToFill;
            // No need for setAttribute("value", ...) as .value is direct
            element.dispatchEvent(new Event('input', {
              bubbles: true
            })); // Simulate user input
            // Consider dispatching 'change' event as well for input fields if needed by frameworks
            // element.dispatchEvent(new Event('change', { bubbles: true }));
          }
          break;
        }
      }
      // Removed .select() and .blur() as they are generally not needed for prefilling
      // and can interfere with user experience.
    });
  }


  /**
   * Fills specific form fields based on query parameters or webinar data.
   * This method directly modifies the form DOM.
   * @param {HTMLFormElement} form The actual HTML form element or a container element.
   * @param {Array<Object>} properties An array of objects defining properties to fill (e.g., [{ param: 'AFFID', property: 'affiliate_id' }]).
   * @param {Object} webinar Webinar data object.
   */
  onSubmitFill(form, properties, webinar) {
    if (!form || !properties || !Array.isArray(properties) || !webinar) {
      console.warn("UserService.onSubmitFill: Invalid parameters provided (form, properties, or webinar).");
      return;
    }

    properties.forEach(item => {
      let value = '';
      switch (item.param) {
        case 'AFFID': {
          value = this.getQueryParam('AFFID'); // Assuming 'value' is declared outside this switch
          break;
        }
        case 'fb': {
          value = this.getQueryParam('fb');
          break;
        }
        case 'webinarkey': {
          value = webinar.webinarkey || ''; // Use logical OR for default empty string
          break;
        }
        case 'webinarid': {
          value = webinar.webinarid || '';
          break;
        }
        case 'webinarurl': {
          value = webinar.webinarkey ? `https://attendee.gotowebinar.com/register/${webinar.webinarkey}` : '';
          break;
        }
        default: {
          value = '';
          break; // Always good to have a break in default too
        }
      }

      if (value) {
        // Find the input element within the provided 'form' element by its name
        const inputElement = form.querySelector(`[name="${item.property}"]`);
        if (inputElement) {
          inputElement.value = value;
          // Dispatch a 'change' event to ensure Angular/other libraries react to the update
          inputElement.dispatchEvent(new Event('change', {
            bubbles: true
          }));
        } else {
          console.warn(`UserService.onSubmitFill: Input element with name "${item.property}" not found in the form.`);
        }
      }
    });
  }

  /**
   * Saves (updates) HubSpot contact properties.
   * @param {string} email The email of the contact to update.
   * @param {Object} data The data object containing properties to update.
   * @returns {Promise} An AngularJS $http promise.
   */
  saveHSProperty(email, data) {
    if (!email || !data || typeof data !== 'object') {
      console.error("UserService.saveHSProperty: Email and a valid data object are required.");
      return this.$q.reject(new Error("Email and data are required to save HubSpot property."));
    }
    return this.$http.put(HS_CONTACT_API_BASE + encodeURIComponent(email), data);
  }

  /**
   * Submits data to a HubSpot form.
   * @param {string} formId The ID of the HubSpot form.
   * @param {Array<Object>} fields An array of field objects for the form.
   * @param {string} [title=""] The page title for HubSpot context.
   * @returns {Promise} An AngularJS $http promise.
   */
  saveHSForm(formId, fields, title = "") {
    if (!formId || !Array.isArray(fields) || fields.length === 0) {
      console.error("UserService.saveHSForm: Form ID and a non-empty array of fields are required.");
      return this.$q.reject(new Error("Form ID and fields are required to save HubSpot form."));
    }
    const hs_context = {
      hutk: this.getCookie(HUBSPOT_UTK_COOKIE_NAME),
      pageUrl: this.$location.absUrl().split('?')[0],
      pageName: title
    };
    const data = {
      fields: fields,
      hs_context: hs_context
    };
    return this.$http.post(HS_FORM_API_BASE + formId, data);
  }

  /**
   * Sets a cookie in the browser.
   * @param {string} cname The name of the cookie.
   * @param {string} cvalue The value of the cookie.
   * @param {number} exdays The number of days until the cookie expires.
   * @returns {boolean} True on success, false on invalid input.
   */
  setCookie(cname, cvalue, exdays) {
    if (!cname || cvalue === undefined || exdays === undefined || exdays < 0) {
      console.warn("UserService.setCookie: Invalid parameters provided. Cookie not set.");
      return false;
    }
    const d = new Date();
    d.setTime(d.getTime() + (exdays * 24 * 60 * 60 * 1000));
    const expires = "expires=" + d.toUTCString();
    // Encode cookie value to handle special characters properly
    document.cookie = `${cname}=${encodeURIComponent(cvalue)};${expires};path=/`;
    return true;
  }

  /**
   * Get the value of a cookie by name.
   * @param {string} name The name of the cookie.
   * @returns {string|undefined} The cookie value, or undefined if not found.
   */
  getCookie(name) {
    if (!name || typeof name !== 'string') {
      console.warn("UserService.getCookie: Invalid cookie name provided.");
      return undefined;
    }
    // Prepare the name with an equals sign for comparison
    const nameEQ = name + "=";
    // Split document.cookie by semicolon and space to get individual cookies
    const ca = document.cookie.split(';');
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      // Trim leading spaces from the cookie string
      while (c.charAt(0) === ' ') c = c.substring(1, c.length);
      // If this cookie string begins with the name we want
      if (c.indexOf(nameEQ) === 0) {
        // Return the decoded value after the name and equals sign
        return decodeURIComponent(c.substring(nameEQ.length, c.length));
      }
    }
    return undefined; // Return undefined if the cookie is not found
  }
}

// Export the class for AngularJS registration
export {
  userService
};